import React from "react";
import { oneOf, shape, func, string } from "prop-types";
import { useParams, useLocation, useNavigate } from "react-router";
import { AppBar, Tab, Tabs, Box } from "@mui/material";

import Color from "color";
import AddToCompareCountIndicator from "./AddToCompareCountIndicator";
import AppBreadcrumbs from "./AppBreadcrumbs";
import Search from "./Search";
import { tabKeys, tabConfig } from "../config";
import analytics from "shared/analytics";

function a11yProps(tabKey) {
  return {
    id: `tab-${tabKey}`,
    "aria-controls": `tabpanel-${tabKey}`,
  };
}

const TabLabel = ({ tabKey, activeTabKey }) => {
  if (tabKey === "compare-your-own-pricing") {
    return (
      <AddToCompareCountIndicator
        label={tabConfig[tabKey]}
        active={activeTabKey === "compare-your-own-pricing"}
      />
    );
  }
  return tabConfig[tabKey];
};

TabLabel.propTypes = {
  tabKey: oneOf(tabKeys).isRequired,
  activeTabKey: oneOf(tabKeys),
};

// Wrapper component to inject router hooks
const TabBarWithRouter = (props) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Create a new props object to ensure Redux connect detects changes
  const newProps = {
    ...props,
    params: { ...params }, // Create new object to ensure reference changes
    location: { ...location }, // Create new object to ensure reference changes
    navigate,
  };
  return <TabBar {...newProps} />;
};

class TabBar extends React.PureComponent {
  render() {
    const {
      navigate,
      params: { activeTabKey = tabKeys[0] } = {},
      location: { search = "" } = {},
    } = this.props;

    const handleChange = (event, tabKey) => {
      analytics.trackTabNavigation(tabKey);
      const path = `/${tabKey}${search ? search : ""}`;
      navigate(path);
    };

    const getTextColor = (color, palette) => {
      if (Color(color).isLight()) return palette.text.primary;
      return palette.common.white;
    };

    return (
      <AppBar
        id="tab-bar"
        position="static"
        component="div"
        role="navigation"
        aria-label="Evidence Compendium Navigation"
        sx={(theme) => ({
          flexDirection: "row",
          background: theme.palette.secondary.main,
          marginBottom: 0,
          boxShadow: "none",
          paddingTop: theme.spacing(1),
          paddingLeft: theme.spacing(4),
          paddingRight: theme.spacing(4),
          alignItems: "flex-end",
          height: "min-content",
          minHeight: "min-content",
          maxHeight: "min-content",
        })}
      >
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <AppBreadcrumbs location={location} activeTabKey={activeTabKey} />
          <Tabs
            value={activeTabKey}
            onChange={handleChange}
            aria-label="Table tabs"
            sx={{
              marginBottom: 0,
              minHeight: "24px",
              alignItems: "flex-end",
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            {tabKeys.map((tabKey) => (
              <Tab
                key={tabKey}
                value={tabKey}
                tabIndex={0}
                label={<TabLabel tabKey={tabKey} activeTabKey={activeTabKey} />}
                id={a11yProps(tabKey).id}
                aria-controls={a11yProps(tabKey)["aria-controls"]}
                sx={(theme) => ({
                  opacity: 1,
                  overflow: "initial",
                  padding: "0px",
                  minHeight: "24px",
                  paddingLeft: theme.spacing(2),
                  paddingRight: theme.spacing(2),
                  paddingBottom: theme.spacing(1),
                  paddingTop: theme.spacing(1),
                  borderTopLeftRadius: theme.spacing(1),
                  borderTopRightRadius: theme.spacing(1),
                  color: "white",
                  border: "1px solid rgba(255,255,255,.4)",
                  fontSize: "16px",
                  borderBottom: "none",
                  transition: "0.2s",
                  marginRight: "3px",
                  zIndex: 2,
                  marginTop: theme.spacing(0.5),
                  textTransform: "initial",
                  [theme.breakpoints.up("md")]: {
                    minWidth: 100,
                  },
                  "&:before": {
                    transition: "0.2s",
                  },
                  "&:not(:first-of-type)": {
                    "&:before": {
                      content: '" "',
                      position: "absolute",
                      left: 0,
                      display: "block",
                      height: 20,
                      width: 1,
                      zIndex: 1,
                      marginTop: theme.spacing(0.5),
                      backgroundColor: theme.palette.primary.light,
                    },
                  },
                  "&.Mui-selected:before": {
                    opacity: 0,
                  },
                  "&:focus:not(.Mui-selected)": {
                    backgroundColor: "rgba(255,255,255,.8)",
                  },
                  "&.Mui-focusVisible:not(.Mui-selected)": {
                    backgroundColor: "rgba(255,255,255,.8)",
                  },
                  "&:hover,&:focus": {
                    "&:not(.Mui-selected)": {
                      backgroundColor: "rgba(255,255,255,.7)",
                      color: "black",
                    },
                    "&::before": {
                      opacity: 0,
                    },
                    "&.MuiTab-root:before": {
                      opacity: 0,
                    },
                  },
                  "&.Mui-selected": {
                    backgroundColor: "white",
                    color: getTextColor("white", theme.palette),
                    fontWeight: 800,
                    "&.MuiTab-root": {
                      zIndex: 1,
                    },
                    "&.MuiTab-root:before": {
                      opacity: 0,
                    },
                    "&:focus, &.Mui-focusVisible": {
                      backgroundColor: "white",
                    },
                  },
                })}
              />
            ))}
          </Tabs>
        </Box>
        <Box
          sx={(theme) => ({
            marginLeft: "auto",
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
          })}
        >
          <Search />
        </Box>
      </AppBar>
    );
  }
}

TabBar.propTypes = {
  location: shape({
    pathname: string,
    search: string,
  }),
  params: shape({
    activeTabKey: oneOf(tabKeys),
    detailId: string,
  }),
  navigate: func.isRequired,
};

// No longer connected: the only mapped prop was `detailName`, from a selector chain
// that had returned "" since /api/analyses stopped sending asset groups in 2024.
export default TabBarWithRouter;
